import type { Dayjs } from 'dayjs';

/**
 * Anything a call site can hold for a calendar day: a picker's Dayjs, a moment
 * (UserPage and the ABM forms are still on moment), a native Date, or a string
 * that came back from the API.
 */
export type CalendarDateSource =
  | Dayjs
  | Date
  | string
  | { format: (template: string) => string };

/**
 * Calendar-date helpers for everything the app sends to the server.
 *
 * A calendar date has no timezone. It is sent as a plain 'YYYY-MM-DD' string
 * and rendered from that string, so there is never an instant for a browser
 * zone to shift. This is the frontend half of the backend's CalendarDate
 * contract — the frontend needs no equivalent class, because it never does
 * arithmetic on stored dates, only on picker values, which are local by design.
 */

/** First day of the month `d` falls in, as 'YYYY-MM-DD'. */
export const toIsoMonth = (d: Dayjs): string =>
  d.startOf('month').format('YYYY-MM-DD');

/** Last day of the month `d` falls in, as 'YYYY-MM-DD'. */
export const toIsoMonthEnd = (d: Dayjs): string =>
  d.endOf('month').format('YYYY-MM-DD');

/**
 * The calendar day a value represents, as 'YYYY-MM-DD'.
 *
 * Accepts every shape that reaches a mutation payload: a picker's Dayjs, a
 * moment, a native Date, and a string that came back from the API and is being
 * sent straight out again. A string is TRUNCATED, never parsed — parsing it
 * would resolve it in some zone, which is the whole defect. `'1987-11-01'` and
 * `'1987-11-01T03:00:00.000Z'` therefore both give `'1987-11-01'`.
 */
export const toIsoDate = (d: CalendarDateSource): string => {
  if (typeof d === 'string') return d.slice(0, 10);
  // A Dayjs is formatted in its OWN mode. Pickers seeded with `dayjs.utc(...)`
  // hand back a UTC-mode value, and converting it to a Date first would read
  // the local day — the previous one for anyone behind UTC. A bare Date has no
  // mode, so the local day is the day the picker showed.
  if (!(d instanceof Date)) return d.format('YYYY-MM-DD');
  return [
    String(d.getFullYear()).padStart(4, '0'),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
};

/**
 * Renders a stored calendar date without consulting any timezone.
 *
 * Accepts either the 'YYYY-MM-DD' the server will send once the Date scalar is
 * split, or the UTC-midnight instant it sends today — both describe the same
 * calendar day, and neither is resolved in the ambient zone.
 */
export const formatCalendarDate = (
  value: string | Date | null | undefined,
  separator = '-',
): string => {
  if (!value) return '';
  const iso =
    typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10);
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return '';
  return [day, month, year].join(separator);
};
